import { Module } from 'vuex';

import Vue from 'vue';

import { RootState, FormState } from '../types';
import {
	newTemplate,
	BASIC_FIELD_TYPES,
	BASIC_FIELDS,
} from '../../constants/form';
import { api } from '../../plugins/salut-api';
import {
	MultiLangText,
	FormSection,
	Dependency,
	Template,
	FormTypeCode,
	ContactTypeCode,
	FormTypeItem,
	TableItemsOptions,
	Properties,
	BasicFieldType,
	ContactExtraSetting,
} from '../../types';
import cloneDeep from 'lodash/cloneDeep';

export const SET_LAST_ERROR = 'SET_LAST_ERROR';
export const SHOW_LOADING = 'SHOW_LOADING';
export const HIDE_LOADING = 'HIDE_LOADING';
export const SET_TEMPLATES = 'SET_TEMPLATES';
export const SET_CURRENT_TEMPLATE = 'SET_CURRENT_TEMPLATE';
export const SET_PRESET_FIELDS = 'SET_PRESET_FIELDS';
export const SET_CUSTOM_FIELDS = 'SET_CUSTOM_FIELDS';
export const SET_FORMS = 'SET_FORMS';
export const SET_CURRENT_SUBMIT_FORM = 'SET_CURRENT_SUBMIT_FORM';
export const CLEAR_URL = 'CLEAR_URL';
export const SET_URL = 'SET_URL';
export const SET_EDITING_MODE = 'SET_EDITING_MODE';
export const SET_FORM_TYPES = 'SET_FORM_TYPES';
export const SET_PLAN_EDITING = 'SET_PLAN_EDITING';
export const SET_CURRENT_TEMPLATE_PLANS = 'SET_CURRENT_TEMPLATE_PLANS';
export const SET_CURRENT_TEMPLATE_AUTO_APPROVE =
	'SET_CURRENT_TEMPLATE_AUTO_APPROVE';
export const SET_FIELD = 'SET_FIELD';
export const SET_DISPLAY_SETTINGS = 'SET_DISPLAY_SETTINGS';
export const SET_SECTION = 'SET_SECTION';
export const SET_HEADER = 'SET_HEADER';
export const SET_FOOTER = 'SET_FOOTER';
export const SET_SECTION_NAME = 'SET_SECTION_NAME';
export const ADD_SECTION = 'ADD_SECTION';
export const DELETE_SECTION = 'DELETE_SECTION';
export const DELETE_FIELD = 'DELETE_FIELD';
export const ADD_FIELD_KEY_TO_SECTION = 'ADD_FIELD_KEY_TO_SECTION';
export const DELETE_FIELD_KEY = 'DELETE_FIELD_KEY';
export const ADD_ELEMENT_KEY = 'ADD_ELEMENT_KEY';
export const DELETE_ELEMENT_KEY = 'DELETE_ELEMENT_KEY';
export const DELETE_RULE = 'DELETE_RULE';
export const SET_CLONE_FIELDS = 'SET_CLONE_FIELDS';
const SET_DRAFT_TABLE_DATA = 'SET_DRAFT_TABLE_DATA';
const SET_SUBMITTED_TABLE_DATA = 'SET_SUBMITTED_TABLE_DATA';
const SET_FORM_TABLE_DATA = 'SET_FORM_TABLE_DATA';
export const FILL_SECTIONS_WITH_ID = 'FILL_SECTIONS_WITH_ID';
const SET_INSTRUCTIONS = 'SET_INSTRUCTIONS';

export const FormActionKeys = {
	fetchDraftTemplates: 'fetchDraftTemplates',
	fetchSubmittedTemplates: 'fetchSubmittedTemplates',
	fetchPublicForms: 'fetchPublicForms',
	fetchFormTypes: 'fetchFormTypes',
	fetchTemplates: 'fetchTemplates',
	fetchPresetFields: 'fetchPresetFields',
	fetchCustomFields: 'fetchCustomFields',
	createTemplate: 'createTemplate',
	updateTemplate: 'updateTemplate',
	updateTemplateStatus: 'updateTemplateStatus',
	fetchForms: 'fetchForms',
	fetchSubmitForm: 'fetchSubmitForm',
	clearSubmitForm: 'clearSubmitForm',
	setEditing: 'setEditing',
	unsetEditing: 'unsetEditing',
	setCurrentTemplate: 'setCurrentTemplate',
	setInstructions: 'setInstructions',
	setPlanEditing: 'setPlanEditing',
	unsetPlanEditing: 'unsetPlanEditing',
	addPlanToCurrentTemplate: 'addPlanToCurrentTemplate',
	removePlanFromCurrentTemplate: 'removePlanFromCurrentTemplate',
	setAutoApproveOfCurrentTemplate: 'setAutoApproveOfCurrentTemplate',
	setFields: 'setFields',
	deleteSection: 'deleteSection',
};

export const FormGetterKeys = {
	loading: 'loading',
	lastError: 'lastError',
	draftTableData: 'draftTableData',
	submittedTableData: 'submittedTableData',
	formTableData: 'formTableData',
	isEditing: 'isEditing',
	templates: 'templates',
	formTypes: 'formTypes',
	planEditing: 'planEditing',
	getFormType: 'getFormType',
	draftForms: 'draftForms',
	pendingForms: 'pendingForms',
	currentTemplate: 'currentTemplate',
	extraFields: 'extraFields',
	displaySettings: 'displaySettings',
	fields: 'fields',
	basicFieldsType: 'basicFieldsType',
	basicFields: 'basicFields',
	presetFields: 'presetFields',
	customFields: 'customFields',
	filteredBasicFields: 'filteredBasicFields',
	filteredPresetFields: 'filteredPresetFields',
	filteredCustomFields: 'filteredCustomFields',
	forms: 'forms',
	publicUrl: 'publicUrl',
	currentTemplatePlanIds: 'currentTemplatePlanIds',
	cloneFields: 'cloneFields',
	areFieldsDuplicate: 'areFieldsDuplicate',
};

const initialState: FormState = {
	loading: 0,
	lastError: '',
	isEditing: false,
	publicUrl: '',
	templates: [],
	currentTemplate: undefined,
	basicFieldsType: BASIC_FIELD_TYPES,
	basicFields: BASIC_FIELDS,
	presetFields: [],
	customFields: [],
	profileDictionaries: {
		member: [],
		volunteer: [],
		donor: [],
		partner: [],
		participant: [],
		jobParticipant: [],
		beneficiary: [],
		case: [],
	},
	contactDictionaries: [],
	forms: [],
	currentSubmitForm: null,
	formTypes: [],
	planEditing: false,
	draftTableData: { items: [], total: 0 },
	submittedTableData: { items: [], total: 0 },
	formTableData: { items: [], total: 0 },
	cloneFields: {},
};

function deleteFieldRecursive(
	state: FormState,
	{ key, arrayParentFieldKey }: { key: string; arrayParentFieldKey?: string },
): void {
	if (!state.currentTemplate) {
		throw 'Missing currentTemplate';
	}
	const fieldKey =
		arrayParentFieldKey &&
		// not self
		arrayParentFieldKey !== key
			? [arrayParentFieldKey, key].join('_')
			: key;
	const field = state.currentTemplate.settings.extraFields.fields[fieldKey];
	if (!field)
		// already gone somehow. Do not throw error
		return;

	if (field.type === BasicFieldType.ARRAY)
		// set own field key as children's arrayParentFieldKey
		arrayParentFieldKey = arrayParentFieldKey || fieldKey;

	const dependencies = field.dependencies;
	const elements = field.elements;
	if (dependencies) {
		dependencies.forEach((dependency: Dependency) => {
			dependency.fields?.forEach((rawDependencyFieldKey: string) => {
				deleteFieldRecursive(state, {
					key: rawDependencyFieldKey,
					arrayParentFieldKey,
				});
				Vue.delete(
					state.currentTemplate!.settings.extraFields.fields,
					arrayParentFieldKey
						? [arrayParentFieldKey, rawDependencyFieldKey].join('_')
						: rawDependencyFieldKey,
				);
			});
		});
	}
	if (elements) {
		elements.forEach((rawElementKey: string) => {
			deleteFieldRecursive(state, { key: rawElementKey, arrayParentFieldKey });
			Vue.delete(
				state.currentTemplate!.settings.extraFields.fields,
				arrayParentFieldKey
					? [arrayParentFieldKey, rawElementKey].join('_')
					: rawElementKey,
			);
		});
	}
	Vue.delete(state.currentTemplate.settings.extraFields.fields, fieldKey);
}

const store: Module<FormState, RootState> = {
	namespaced: true,

	state: { ...initialState },

	actions: {
		async [FormActionKeys.fetchDraftTemplates](
			{ commit },
			options: TableItemsOptions,
		) {
			try {
				commit(SHOW_LOADING);

				const { result, total } = await api.fetchDraftTemplateList(options);

				commit(SET_DRAFT_TABLE_DATA, { result, total });
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.fetchSubmittedTemplates](
			{ commit },
			options: TableItemsOptions,
		) {
			try {
				commit(SHOW_LOADING);

				const { result, total } = await api.fetchSubmittedTemplateList(options);

				commit(SET_SUBMITTED_TABLE_DATA, { result, total });
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.fetchPublicForms](
			{ commit },
			options: TableItemsOptions,
		) {
			try {
				commit(SHOW_LOADING);

				const { result, total } = await api.fetchFormList(options);

				commit(SET_FORM_TABLE_DATA, { result, total });
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.fetchFormTypes](
			{ commit },
			{
				hasMemberFeature,
				hasVolunteerFeature,
			}: { hasMemberFeature: boolean; hasVolunteerFeature: boolean },
		) {
			try {
				commit(SHOW_LOADING);
				const filter: string[] = [];
				if (!hasMemberFeature) filter.push('member');
				if (!hasVolunteerFeature) filter.push('volunteer');

				let formTypes = await api.fetchFormTypes();
				formTypes = formTypes.filter(
					type => !filter.includes(type.contactType),
				);
				commit(SET_FORM_TYPES, formTypes);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.fetchTemplates]({ commit }) {
			try {
				commit(SHOW_LOADING);
				commit(SET_TEMPLATES, await api.fetchTemplates());
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.fetchPresetFields]({ commit }) {
			try {
				commit(SHOW_LOADING);
				commit(SET_PRESET_FIELDS, await api.fetchPresetFields());
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.fetchCustomFields]({ commit }) {
			try {
				commit(SHOW_LOADING);
				commit(SET_CUSTOM_FIELDS, await api.fetchCustomFields());
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.createTemplate]({ commit }, template) {
			try {
				commit(SHOW_LOADING);
				template.settings.extraFields.displaySettings.forEach(
					(displaySettings: FormSection) => {
						delete displaySettings.id;
					},
				);
				await api.createTemplate(template);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.updateTemplate]({ commit }, form) {
			try {
				commit(SHOW_LOADING);
				form.settings.extraFields.displaySettings.forEach(
					(displaySettings: FormSection) => {
						delete displaySettings.id;
					},
				);
				await api.updateTemplate(form);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.updateTemplateStatus]({ commit }, params) {
			try {
				commit(SHOW_LOADING);
				// commit(CLEAR_URL);
				await api.updateTemplateStatus(params);
				// commit(SET_URL, data.publicId);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.fetchForms]({ commit }) {
			try {
				commit(SHOW_LOADING);
				commit(SET_FORMS, await api.fetchAllFormsWithOnlyIdAndName());
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [FormActionKeys.fetchSubmitForm]({ commit }, { submitFormId }) {
			try {
				commit(SHOW_LOADING);
				commit(
					SET_CURRENT_SUBMIT_FORM,
					await api.fetchSubmitForm(submitFormId),
				);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		[FormActionKeys.clearSubmitForm]({ commit }) {
			commit(SET_CURRENT_SUBMIT_FORM, null);
		},

		[FormActionKeys.setEditing]({ commit }) {
			commit(SET_EDITING_MODE, true);
		},
		[FormActionKeys.unsetEditing]({ commit }) {
			commit(SET_EDITING_MODE, false);
		},

		[FormActionKeys.setCurrentTemplate]({ commit }, { template }) {
			try {
				commit(SHOW_LOADING);
				commit(SET_CURRENT_TEMPLATE, template);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		[FormActionKeys.setInstructions]({ commit }, { instructions }) {
			commit(SET_INSTRUCTIONS, instructions);
		},

		[FormActionKeys.setPlanEditing]({ commit }) {
			commit(SET_PLAN_EDITING, true);
		},

		[FormActionKeys.unsetPlanEditing]({ commit }) {
			commit(SET_PLAN_EDITING, false);
		},

		[FormActionKeys.addPlanToCurrentTemplate](
			{ commit, getters },
			{ planId }: { planId: string },
		) {
			// skip if exists
			if (getters.currentTemplatePlanIds.includes(planId)) return;
			commit(SET_CURRENT_TEMPLATE_PLANS, [
				...getters.currentTemplatePlanIds,
				planId,
			]);
		},

		[FormActionKeys.removePlanFromCurrentTemplate](
			{ commit, getters },
			{ planId }: { planId: string },
		) {
			// skip if not exists
			if (!getters.currentTemplatePlanIds.includes(planId)) return;
			commit(
				SET_CURRENT_TEMPLATE_PLANS,
				getters.currentTemplatePlanIds.filter(
					(onePlanId: string) => onePlanId !== planId,
				),
			);
		},
		[FormActionKeys.setAutoApproveOfCurrentTemplate](
			{ commit },
			{ autoApprove }: { autoApprove: boolean },
		) {
			commit(SET_CURRENT_TEMPLATE_AUTO_APPROVE, Boolean(autoApprove));
		},

		[FormActionKeys.setFields](
			{ commit },
			fields: { key: string; properties: Properties }[],
		) {
			for (const { key, properties } of fields) {
				commit(SET_FIELD, { key, properties: cloneDeep(properties) });
			}
		},
		[FormActionKeys.deleteSection](
			{ commit },
			{ section }: { section: FormSection },
		) {
			for (const fieldKey of section.fields) {
				commit(DELETE_FIELD, { key: fieldKey });
			}
			commit(DELETE_SECTION, section);
		},
	},

	mutations: {
		[SET_DRAFT_TABLE_DATA](state, { result, total }) {
			state.draftTableData = {
				items: result,
				total: total,
			};
		},

		[SET_SUBMITTED_TABLE_DATA](state, { result, total }) {
			state.submittedTableData = {
				items: result,
				total: total,
			};
		},

		[SET_FORM_TABLE_DATA](state, { result, total }) {
			state.formTableData = {
				items: result,
				total: total,
			};
		},

		[SET_EDITING_MODE](state, bool) {
			state.isEditing = bool;
		},

		[SET_CLONE_FIELDS](state, cloneFields) {
			state.cloneFields = { ...cloneFields };
		},

		[SET_FORM_TYPES](state, formTypes) {
			state.formTypes = formTypes;
		},

		[SET_TEMPLATES](state, templates) {
			state.templates = [...templates];
		},

		[SET_CURRENT_TEMPLATE](state, form) {
			state.currentTemplate = form;
		},

		[SET_PRESET_FIELDS](state, fields) {
			state.presetFields = [...fields];
		},

		[SET_CUSTOM_FIELDS](state, fields) {
			state.customFields = [...fields];
		},

		[SHOW_LOADING](state) {
			state.loading += 1;
		},

		[HIDE_LOADING](state) {
			state.loading -= 1;
		},

		[SET_LAST_ERROR](state, error) {
			state.lastError = error;
		},

		[SET_URL](state, url) {
			state.publicUrl = url;
		},

		[CLEAR_URL](state) {
			state.publicUrl = '';
		},

		[SET_INSTRUCTIONS](state, instructions) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			state.currentTemplate.instructions = instructions;
		},

		[SET_DISPLAY_SETTINGS](state, displaySettings) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			state.currentTemplate.settings.extraFields.displaySettings = [
				...displaySettings,
			];
		},

		[SET_SECTION](state, section) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			const sectionIndex = state.currentTemplate.settings.extraFields.displaySettings.findIndex(
				_section => section.id === _section.id,
			);
			if (sectionIndex > -1) {
				Vue.set(
					state.currentTemplate.settings.extraFields.displaySettings,
					sectionIndex,
					section,
				);
			}
		},

		[SET_HEADER](state, header) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			state.currentTemplate.settings.header = { ...header };
		},

		[SET_FOOTER](state, footer) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			state.currentTemplate.settings.footer = { ...footer };
		},

		[SET_SECTION_NAME](
			state,
			props: { sectionIndex: number; name: MultiLangText },
		) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			if (
				!state.currentTemplate.settings.extraFields.displaySettings[
					props.sectionIndex
				]
			)
				return;
			Vue.set(
				state.currentTemplate.settings.extraFields.displaySettings[
					props.sectionIndex
				],
				'groupName',
				props.name,
			);
		},

		[ADD_SECTION](state, index: number): void {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			state.currentTemplate.settings.extraFields.displaySettings.splice(
				index,
				0,
				{
					id:
						Date.now().toString() + Math.floor(Math.random() * Math.floor(100)),
					fields: [],
					groupName: {
						en: 'Untitled Section',
					},
				},
			);
		},

		[DELETE_SECTION](state, section: FormSection): void {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			const sectionIndex = state.currentTemplate.settings.extraFields.displaySettings.findIndex(
				_section => section.id === _section.id,
			);
			Vue.delete(
				state.currentTemplate.settings.extraFields.displaySettings,
				sectionIndex,
			);
		},

		[SET_FIELD](state, field) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			// keep mapId
			field.properties.mapId =
				field.properties.mapId ||
				state.currentTemplate.settings.extraFields.fields[field.key]?.mapId ||
				undefined;
			Vue.set(
				state.currentTemplate.settings.extraFields.fields,
				field.key,
				field.properties,
			);
		},

		[DELETE_FIELD](
			state,
			{
				key,
				arrayParentFieldKey,
			}: { key: string; arrayParentFieldKey?: string },
		): void {
			deleteFieldRecursive(state, { key, arrayParentFieldKey });
		},

		[ADD_FIELD_KEY_TO_SECTION](
			state,
			props: { key: string; sectionId: string; positionKey: string | null },
		): void {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			const sectionIndex = state.currentTemplate.settings.extraFields.displaySettings.findIndex(
				displaySetting => displaySetting.id == props.sectionId,
			);
			if (sectionIndex > -1) {
				const section =
					state.currentTemplate.settings.extraFields.displaySettings[
						sectionIndex
					];
				const fieldIndex = section.fields.findIndex(
					fieldKey => fieldKey == props.positionKey,
				);
				section.fields.splice(
					fieldIndex > -1 ? 0 : fieldIndex + 1,
					0,
					props.key,
				);
			}
		},

		[DELETE_FIELD_KEY](state, props: { key: string; sectionId: string }): void {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			const section = state.currentTemplate.settings.extraFields.displaySettings.find(
				displaySetting => displaySetting.id == props.sectionId,
			);
			if (section) {
				const index = section.fields.findIndex(
					fieldKey => fieldKey == props.key,
				);
				if (index > -1) Vue.delete(section.fields, index);
			}
		},

		[ADD_ELEMENT_KEY](
			state,
			props: { key: string; fieldKey: string; positionKey: string },
		): void {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			const field =
				state.currentTemplate.settings.extraFields.fields[props.fieldKey];
			if (field.elements) {
				const index = field.elements.findIndex(
					elementKey => elementKey == props.positionKey,
				);
				field.elements.length == 0
					? (field.elements = [props.key])
					: field.elements.splice(index, 0, props.key);
			} else if (field.dependencies) {
				const dependency = field.dependencies.find(dependency =>
					dependency.fields?.includes(props.positionKey),
				);
				if (dependency?.fields) {
					const index = dependency.fields?.findIndex(
						key => key == props.positionKey,
					);
					dependency.fields.splice(index, 0, props.key);
				}
			}
		},

		[DELETE_ELEMENT_KEY](
			state,
			props: { key: string; fieldKey: string },
		): void {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			const field =
				state.currentTemplate.settings.extraFields.fields[props.fieldKey];
			if (field.elements) {
				const elementIndex = field.elements.findIndex(
					elementKey => elementKey === props.key,
				);
				if (elementIndex >= -1) Vue.delete(field.elements, elementIndex);
			} else if (field.dependencies) {
				const dependency = field.dependencies.find(dependency =>
					dependency.fields?.includes(props.key),
				);
				if (dependency?.fields) {
					const index = dependency.fields?.findIndex(key => key == props.key);
					dependency.fields.splice(index, 1);
				}
			}
		},

		[DELETE_RULE](
			state,
			props: {
				rule: Dependency;
				fieldKey: string;
				arrayParentFieldKey?: string;
			},
		) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			const arrayParentFieldKey = props.arrayParentFieldKey;
			for (const rawChildFieldKey of props.rule.fields ?? []) {
				const childFieldKey = arrayParentFieldKey
					? [arrayParentFieldKey, rawChildFieldKey].join('_')
					: rawChildFieldKey;
				Vue.delete(
					state.currentTemplate.settings.extraFields.fields,
					childFieldKey,
				);
			}
			const fieldKey = arrayParentFieldKey
				? [arrayParentFieldKey, props.fieldKey].join('_')
				: props.fieldKey;
			const field = state.currentTemplate.settings.extraFields.fields[fieldKey];
			if (field.dependencies) {
				const dependencyIndex = field.dependencies.findIndex(
					dependency => dependency.id == props.rule.id,
				);
				if (dependencyIndex > -1) {
					Vue.delete(field.dependencies, dependencyIndex);
				}
			}
		},

		[SET_FORMS](state, forms) {
			state.forms = forms;
		},
		[SET_CURRENT_SUBMIT_FORM](state, submitForm) {
			state.currentSubmitForm = submitForm;
		},

		[SET_PLAN_EDITING](state, isEditing) {
			state.planEditing = isEditing;
		},

		[SET_CURRENT_TEMPLATE_PLANS](state, planIds) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			if (!state.currentTemplate.extraSetting)
				Vue.set(state.currentTemplate, 'extraSetting', {});
			Vue.set(
				state.currentTemplate.extraSetting as ContactExtraSetting,
				'planIds',
				planIds,
			);
		},

		[SET_CURRENT_TEMPLATE_AUTO_APPROVE](state, autoApprove: boolean) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			if (!state.currentTemplate.extraSetting)
				Vue.set(state.currentTemplate, 'extraSetting', {});
			(state.currentTemplate
				.extraSetting as ContactExtraSetting).autoApprove = autoApprove;
		},

		[FILL_SECTIONS_WITH_ID](state) {
			if (!state.currentTemplate) {
				throw 'Missing currentTemplate';
			}
			state.currentTemplate.settings.extraFields.displaySettings = state.currentTemplate.settings.extraFields.displaySettings.map(
				section => ({
					...section,
					id:
						section.id ||
						Date.now().toString() +
							Math.floor(Math.random() * Math.floor(1000)),
				}),
			);
		},
	},

	getters: {
		[FormGetterKeys.loading]: state => Boolean(state.loading),
		[FormGetterKeys.lastError]: state => state.lastError,

		[FormGetterKeys.draftTableData]: state => state.draftTableData,
		[FormGetterKeys.submittedTableData]: state => state.submittedTableData,
		[FormGetterKeys.formTableData]: state => state.formTableData,

		[FormGetterKeys.isEditing]: state => state.isEditing,
		[FormGetterKeys.templates]: state => state.templates,
		[FormGetterKeys.formTypes]: state => state.formTypes,
		[FormGetterKeys.planEditing]: state => state.planEditing,
		[FormGetterKeys.getFormType]: (_, getters) => (
			formTypeCode: FormTypeCode,
			contactTypeCode: ContactTypeCode,
		) =>
			getters.formTypes.find(
				(item: FormTypeItem) =>
					item.formType === formTypeCode &&
					item.contactType === contactTypeCode,
			),

		[FormGetterKeys.draftForms]: state =>
			state.templates
				.filter(template => template.status === 'draft')
				.map((template: Template) => {
					return {
						...template,
						plan: template.extraSetting?.planIds,
						autoApprove: template.extraSetting?.autoApprove,
					};
				}),

		[FormGetterKeys.pendingForms]: state =>
			state.templates
				.filter(
					template =>
						template.status === 'pending' || template.status === 'approved',
				)
				.map((template: Template) => {
					return {
						...template,
						plan: template.extraSetting?.planIds,
						autoApprove: template.extraSetting?.autoApprove,
					};
				}),

		[FormGetterKeys.currentTemplate]: state => state.currentTemplate,
		[FormGetterKeys.extraFields]: state =>
			state.currentTemplate?.settings.extraFields,
		[FormGetterKeys.displaySettings]: state =>
			state.currentTemplate?.settings.extraFields.displaySettings.map(
				(displaySettings: FormSection) => ({
					...displaySettings,
					id:
						displaySettings.id ||
						Date.now().toString() +
							Math.floor(Math.random() * Math.floor(1000)),
				}),
			),
		[FormGetterKeys.fields]: state =>
			state.currentTemplate?.settings.extraFields.fields,

		[FormGetterKeys.basicFieldsType]: state => state.basicFieldsType,
		[FormGetterKeys.basicFields]: state => state.basicFields,
		[FormGetterKeys.presetFields]: state => state.presetFields,
		[FormGetterKeys.customFields]: state => state.customFields,

		[FormGetterKeys.filteredBasicFields]: state => (search: string) =>
			state.basicFields.filter(basicField => {
				return basicField.type?.toLowerCase().includes(search.toLowerCase());
			}),

		[FormGetterKeys.filteredPresetFields]: state => (search: string) =>
			state.presetFields.filter(presetField => {
				return presetField.displayKeys
					?.join()
					.toLowerCase()
					.includes(search.toLowerCase());
			}),

		[FormGetterKeys.filteredCustomFields]: state => (search: string) =>
			state.customFields.filter(customField => {
				return customField.name?.toLowerCase().includes(search.toLowerCase());
			}),

		[FormGetterKeys.forms]: state => state.forms,
		[FormGetterKeys.publicUrl]: state => state.publicUrl,
		[FormGetterKeys.currentTemplatePlanIds]: state =>
			state.currentTemplate?.extraSetting?.planIds || [],
		[FormGetterKeys.cloneFields]: state => state.cloneFields,
		/**
		 * Check duplicates. Return true means duplicate
		 */
		[FormGetterKeys.areFieldsDuplicate]: (_, getters) => (
			keyPrefix?: string,
		) => {
			for (const key in getters.cloneFields) {
				const targetKey = keyPrefix ? `${keyPrefix}${key}` : key;
				if (getters.currentTemplate.settings.extraFields.fields[targetKey]) {
					return true;
				}
			}
			return false;
		},
	},
};

export default store;
